import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as ArrowLeftIcon } from "assets/svgs/ArrowLeft.svg";
import ActionList from "./ActionList";
import StartHereConfirmDialog from "./StartHereConfirmDialog";
import PayloadHealthWarningDialog from "./PayloadHealthWarningDialog";
import { usePayloadHealthMissionStart } from "lib/mission/usePayloadHealthMissionStart";
import { missionStartStepIndexForActionId } from "lib/mission/payloadHealthCheck";
import { useGetMissionQuery } from "../../graphql/missions.generated";
import { useGetActionsQuery } from "../../graphql/actions.generated";
import { Action } from "app/services/types.generated";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";
import {
  useGetBehaviourMissionProfileQuery,
  useGetBehaviourProfilesQuery,
  useSetBehaviourMissionProfileMutation,
} from "app/services/behaviourRulesApi";
import { formatLocalDateTime, LOCAL_DATETIME_MEDIUM_SHORT } from "lib/utils/dateTimeDisplay";
import { Link } from "react-router-dom";
import ReadOnlyMissionSubpage from "./ReadOnlyMissionSubpage";

interface MissionSubpageProps {
  missionId?: string;
  sourceArcSerial?: string;
  readOnly?: boolean;
  showBackButton?: boolean;
  onBack: () => void;
}

const ControlMissionSubpage: React.FC<MissionSubpageProps> = ({
  missionId = "",
  showBackButton = true,
  onBack,
}) => {
  const { t } = useTranslation(["missions", "common"]);
  const TERMINAL_MISSION_STATUSES = new Set(["idle", "completed", "failed", "cancelled", "canceled", "succeeded"]);
  const { data: missionData, isLoading: isMissionLoading } = useGetMissionQuery(
    { id: missionId },
    { skip: !missionId }
  );
  const { data: actionsData, isLoading: isActionsLoading } = useGetActionsQuery(
    { missionId },
    { skip: !missionId }
  );
  const [startHereMode, setStartHereMode] = useState(false);
  const [startHerePending, setStartHerePending] = useState<{
    action: Action;
    index: number;
  } | null>(null);
  const { data: profilesData } = useGetBehaviourProfilesQuery(undefined);
  const { data: missionProfileData, refetch: refetchMissionProfile } = useGetBehaviourMissionProfileQuery(
    missionId,
    { skip: !missionId }
  );
  const [setBehaviourMissionProfile, { isLoading: isSavingProfile }] = useSetBehaviourMissionProfileMutation();
  const {
    isExecuting,
    currentMissionId,
    executorState,
    executionResult,
    cancelMission,
    pauseMission,
    resumeMission,
    clearExecutionDisplay,
    behaviourActionFeedback,
    stepStatuses,
  } = useMissionExecution();
  const {
    requestMissionStart,
    payloadWarningVisible,
    payloadWarningIssues,
    continueDespitePayloadWarning,
    cancelPayloadWarning,
  } = usePayloadHealthMissionStart();

  const mission = missionData?.mission;
  const actions = (actionsData?.actions || []) as Action[];
  const profiles = profilesData?.profiles ?? [];
  const assignedProfileId = missionProfileData?.assignment?.profile_id ?? "";
  const assignedProfileName =
    profiles.find((profile) => profile.id === assignedProfileId)?.name ?? t("common:notAssigned");

  const handleProfileChange = async (profileId: string) => {
    if (!profileId) return;
    await setBehaviourMissionProfile({ missionId, profileId }).unwrap();
    await refetchMissionProfile();
  };

  // Check if this mission is currently executing
  const normalizedExecutorStatus = String(executorState?.status || "").trim().toLowerCase();
  const executorMissionId = String(executorState?.mission_id || "").trim();
  const isExecutorDrivingThisMission =
    executorMissionId === missionId && !TERMINAL_MISSION_STATUSES.has(normalizedExecutorStatus);
  const isThisMissionExecuting =
    ((isExecuting && currentMissionId === missionId) || isExecutorDrivingThisMission);

  useEffect(() => {
    if (isThisMissionExecuting) {
      setStartHereMode(false);
      setStartHerePending(null);
    }
  }, [isThisMissionExecuting]);

  const executingActionId = useMemo(() => {
    const activeEntry = Object.entries(stepStatuses).find(
      ([, status]) => status.status === "running" || status.status === "paused",
    );
    if (activeEntry) return activeEntry[0];
    if (!isThisMissionExecuting || !executorState) return null;
    const stepIndex = executorState.current_step_index;
    if (stepIndex >= 0 && stepIndex < actions.length) {
      return actions[stepIndex].id;
    }
    return null;
  }, [stepStatuses, isThisMissionExecuting, executorState, actions]);

  // Get execution status text
  const getStatusText = () => {
    if (!isThisMissionExecuting) return null;
    if (normalizedExecutorStatus === "running") {
      const currentStep = executorState?.current_step_index ?? -1;
      const totalSteps = executorState?.total_steps ?? actions.length;
      if (currentStep >= 0) {
        return t("subpage.runningStep", { current: currentStep + 1, total: totalSteps });
      }
      return t("subpage.starting");
    }
    if (normalizedExecutorStatus === "paused") {
      const currentStep = executorState?.current_step_index ?? -1;
      const totalSteps = executorState?.total_steps ?? actions.length;
      if (currentStep >= 0) {
        return t("subpage.pausedStep", { current: currentStep + 1, total: totalSteps });
      }
      return t("subpage.paused");
    }
    if (normalizedExecutorStatus === "returning") {
      return t("subpage.returningToLaunch");
    }
    return normalizedExecutorStatus || t("subpage.running");
  };

  const handleRunMission = () => {
    setStartHereMode(false);
    setStartHerePending(null);
    requestMissionStart(missionId, actions);
  };

  const handleToggleStartHere = () => {
    setStartHerePending(null);
    setStartHereMode((prev) => !prev);
  };

  const handleStartHereSelect = (action: Action, _index: number) => {
    if (action.actionType !== "NavigateTo") return;
    const index = actions.findIndex((item) => item.id === action.id);
    if (index < 0) return;
    setStartHerePending({ action, index });
  };

  const handleStartHereConfirm = () => {
    if (!startHerePending) return;
    const startStepIndex = missionStartStepIndexForActionId(
      actions,
      startHerePending.action.id,
    );
    setStartHerePending(null);
    setStartHereMode(false);
    requestMissionStart(missionId, actions, { startStepIndex });
  };

  const handleStartHereCancel = () => {
    setStartHerePending(null);
  };

  const handleCancelMission = () => {
    cancelMission();
  };

  const handlePauseMission = () => {
    pauseMission();
  };

  const handleResumeMission = () => {
    resumeMission();
  };

  const handleBack = () => {
    setStartHereMode(false);
    setStartHerePending(null);
    clearExecutionDisplay();
    onBack();
  };

  if (isMissionLoading || isActionsLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-r10 bg-black90 p-4">
        <span className="text-h4 text-black60">{t("subpage.loading")}</span>
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="flex min-h-0 flex-1 flex-col rounded-r10 bg-black90 p-4">
        <div className="mb-4 flex w-full shrink-0 items-center justify-between">
          {showBackButton && (
            <FunctionalButton
              icon={<ArrowLeftIcon className="w-6" />}
              className="h-6 w-6"
              onClick={handleBack}
            />
          )}
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <span className="text-h4 text-red-500">{t("subpage.notFound")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-r10 bg-black90">
      {/* Fixed header — Run Mission / Cancel stays visible while scrolling */}
      <div className="shrink-0 border-b border-black80 px-4 pb-3 pt-4">
        <div className="flex w-full items-center justify-between">
          <div className="flex min-w-0 items-center">
            {showBackButton && (
              <FunctionalButton
                icon={<ArrowLeftIcon className="w-6" />}
                className="h-6 w-6 shrink-0"
                onClick={handleBack}
              />
            )}
            <h2 className={`${showBackButton ? "ml-4 " : ""}truncate text-h3`}>{mission.name}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isThisMissionExecuting ? (
              <>
                <span className="text-h5 text-black40">{getStatusText()}</span>
                {normalizedExecutorStatus === "paused" ? (
                  <FunctionalButton
                    title={t("subpage.continue")}
                    className="h-9 rounded-r5 bg-primary2 px-4 py-2 hover:bg-primary1"
                    onClick={handleResumeMission}
                  />
                ) : normalizedExecutorStatus !== "returning" ? (
                  <FunctionalButton
                    title={t("subpage.pause")}
                    className="h-9 rounded-r5 bg-black70 px-4 py-2 hover:bg-black60"
                    onClick={handlePauseMission}
                  />
                ) : null}
                <FunctionalButton
                  title={t("subpage.cancel")}
                  className="h-9 rounded-r5 bg-red-600 px-4 py-2 hover:bg-red-500"
                  onClick={handleCancelMission}
                />
              </>
            ) : (
              <>
                <FunctionalButton
                  title={t("subpage.startHere")}
                  className={`h-9 rounded-r5 px-4 py-2 ${
                    startHereMode
                      ? "bg-primary2 text-white hover:bg-primary1"
                      : "bg-black70 text-black40 hover:bg-black60"
                  }`}
                  onClick={handleToggleStartHere}
                  disabled={isExecuting}
                />
                <FunctionalButton
                  title={t("subpage.runMission")}
                  className="h-9 rounded-r5 bg-primary2 px-4 py-2 hover:bg-primary1"
                  onClick={handleRunMission}
                  disabled={isExecuting}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4">
        {/* Mission metadata */}
        <div className="mb-4 rounded bg-black80 p-3">
          <p className="text-h5 text-black60">
            {t("subpage.created", { datetime: formatLocalDateTime(mission.createdAt, LOCAL_DATETIME_MEDIUM_SHORT) })}
          </p>
          <p className="text-h5 text-black60">
            {t("subpage.actionCount", { count: actions.length })}
          </p>
          <div className="mt-3">
            <p className="mb-1 text-h5 text-black40">{t("subpage.behaviourProfile")}</p>
            <select
              className="w-full rounded-r5 border border-black70 bg-black90 px-3 py-2 text-h5"
              value={assignedProfileId}
              onChange={(e) => handleProfileChange(e.target.value)}
              disabled={isSavingProfile || profiles.length === 0}
            >
              <option value="">{profiles.length === 0 ? t("subpage.noProfiles") : t("subpage.selectProfile")}</option>
              {profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
            {assignedProfileId && (
              <p className="mt-1 text-h5 text-black40">{t("subpage.activeProfile", { name: assignedProfileName })}</p>
            )}
          </div>
          {executorState?.error && (
            <p className="text-h5 text-red-500 mt-2">
              {t("subpage.error", { message: executorState.error })}
            </p>
          )}
        </div>

        {/* Execution result banner */}
        {executionResult && currentMissionId === missionId && (() => {
          const runId = (() => { try { return JSON.parse(executionResult.final_state)?.run_id as string | undefined; } catch { return undefined; } })();
          const navigationCanceled = Boolean(executionResult.navigation_canceled);
          const behaviourMessage = behaviourActionFeedback?.message;
          const behaviourRunning = behaviourActionFeedback?.status === "running";
          const behaviourFailed = behaviourActionFeedback?.status === "failed";
          return (
            <div className="mb-4 space-y-2">
              <div className={`flex items-center justify-between rounded p-3 ${executionResult.success ? 'bg-green-900' : navigationCanceled ? 'bg-amber-950' : 'bg-red-900'}`}>
                <p className="text-h5">
                  {executionResult.success
                    ? t("subpage.missionCompleted")
                    : navigationCanceled
                      ? t("subpage.navigationCanceled")
                      : t("subpage.missionFailed", { message: executionResult.message })}
                </p>
                {executionResult.success && runId && (
                  <Link
                    to={`logs/${runId}`}
                    className="text-h5 text-white hover:text-black40 ml-4 shrink-0"
                  >
                    {t("subpage.viewLog")}
                  </Link>
                )}
              </div>
              {navigationCanceled && (
                <div
                  className={`rounded p-3 ${
                    behaviourFailed ? "bg-red-950" : behaviourRunning ? "bg-blue-950" : "bg-green-950"
                  }`}
                >
                  <p className="text-h5 font-semibold">{t("subpage.behaviourReturn")}</p>
                  <p className="text-h5 text-black20">
                    {behaviourMessage
                      || (behaviourRunning
                        ? t("subpage.waitingBehaviourServer")
                        : t("subpage.noRtlFeedback"))}
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Action List (read-only, scrollable) */}
        <ActionList
          actions={actions}
          onNoteChange={() => { }}
          readOnly={false}
          scrollToActionId={executingActionId}
          scrollBlock="start"
          startHereMode={startHereMode}
          onStartHereSelect={handleStartHereSelect}
        />

      </div>

      <StartHereConfirmDialog
        visible={startHerePending != null}
        waypointName={startHerePending?.action.name ?? ""}
        onConfirm={handleStartHereConfirm}
        onCancel={handleStartHereCancel}
      />

      <PayloadHealthWarningDialog
        visible={payloadWarningVisible}
        issues={payloadWarningIssues}
        onContinue={continueDespitePayloadWarning}
        onCancel={cancelPayloadWarning}
      />
    </div>
  );
};

// Fleet previews must not mount hooks that require a selected ARC session.
const MissionSubpage: React.FC<MissionSubpageProps> = (props) =>
  props.readOnly ? <ReadOnlyMissionSubpage {...props} /> : <ControlMissionSubpage {...props} />;

export default MissionSubpage;
